import { useEffect, useState } from "react";
import { ClockIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";

interface SessionHeaderProps {
  feedTitle: string;
  expiresAt: string;
  onNewSession?: () => void;
}

function formatTimeRemaining(expiresAt: string): string {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const remaining = expiry - now;

  if (remaining <= 0) return "Expired";

  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  }
  return `${minutes}m remaining`;
}

function isExpiringSoon(expiresAt: string): boolean {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const remaining = expiry - now;
  return remaining > 0 && remaining < 30 * 60 * 1000; // 30 minutes
}

export default function SessionHeader({
  feedTitle,
  expiresAt,
  onNewSession,
}: SessionHeaderProps) {
  const [timeRemaining, setTimeRemaining] = useState(formatTimeRemaining(expiresAt));
  const [expiringSoon, setExpiringSoon] = useState(isExpiringSoon(expiresAt));

  useEffect(() => {
    const updateTime = () => {
      setTimeRemaining(formatTimeRemaining(expiresAt));
      setExpiringSoon(isExpiringSoon(expiresAt));
    };

    // Update more frequently when expiring soon
    const interval = setInterval(updateTime, expiringSoon ? 1000 : 60000);
    return () => clearInterval(interval);
  }, [expiresAt, expiringSoon]);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          {feedTitle}
        </h1>
      </div>

      <div className="flex items-center gap-4">
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium",
            expiringSoon
              ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
              : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
          )}
        >
          <ClockIcon className="w-4 h-4" />
          <span>{timeRemaining}</span>
        </div>

        {onNewSession && (
          <button
            onClick={onNewSession}
            className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline underline-offset-2"
          >
            New Session
          </button>
        )}
      </div>
    </div>
  );
}
